/**
 * Assessment Health Thresholds — Declarative SSOT for pool-health policy.
 *
 * ARCHITECTURE v4.0 — S8 / FG-AssessmentPool (mei 2026)
 *
 * This file is the policy-side of Assessment Health governance. It declares
 * "what is a healthy pool size per (module, tier-band)?" — the
 * `assessmentHealthStage` consumes these numbers, never hardcodes them.
 *
 * Design contract (anti-bandaid principles):
 *  - #1 No naked numbers: thresholds are NAMED constants with explicit units.
 *  - #2 No booleans where enums fit: status is an enum ('gap' | 'thin' | 'ok'),
 *       not a "isHealthy: boolean" — it loses the "thin but viable" signal.
 *  - #4 Defaults are diagnostic, not punitive: every threshold has a comfort
 *       band (1.5×) so we coach BEFORE the pool actually breaks.
 *
 * NOTE: this file lives in `governance/pipeline/` (NOT in `assessment/`)
 * because it expresses *governance policy OVER the assessment domain*, not
 * an assessment internal. DDD boundary stays strict.
 */

import type { TierBand } from '../tierBands'
import type { ModuleId } from '../../content'

// ─── Pool Capacity Thresholds (Layer A) ──────────────────────────────────────

/**
 * Minimum viable MC-eligible pool size per tier band.
 *
 * Rationale: a Cito-style toets pulls ≈ 8 questions per (module, tier-band)
 * slice. To avoid the same questions reappearing every session we need a
 * comfortable headroom — minimum = 1× toets, comfort = 1.5× toets.
 *
 * These numbers are intentionally conservative. Layer C (feasibility, later
 * sprint) will tune them empirically once `assessmentBlueprintGenerator`
 * dry-runs land.
 */
export const MINIMUM_VIABLE_POOL_SIZE: Readonly<Record<TierBand, number>> =
  Object.freeze({
    basic: 8, // tiers 1–4   — entry assessments, lower stakes
    intermediate: 10, // tiers 5–8   — mid-curriculum
    advanced: 12, // tiers 9–12  — high-stakes (M8 / E8 entry points)
    expert: 12, // tiers 13–16 — same floor as advanced
  })

/**
 * Per-module overrides. Use only when a module's didactic shape genuinely
 * differs from the global floor (e.g. very narrow domains can ship slightly
 * smaller pools). EMPTY by default — extend deliberately, never reflexively.
 *
 * Example (NOT applied — illustration only):
 *   STU: { basic: 6, intermediate: 8 }   // small-domain module
 */
export const MODULE_POOL_OVERRIDES: Readonly<
  Partial<Record<ModuleId, Partial<Record<TierBand, number>>>>
> = Object.freeze({})

/**
 * Multiplier for "comfortable" pool size. Below comfort but above minimum
 * = `status: 'thin'` (info-level signal). Below minimum = `'gap'` (coach).
 */
export const COMFORT_MULTIPLIER = 1.5 as const

// ─── Pool Status Enum ────────────────────────────────────────────────────────

/**
 * Pool health status per (module, tier-band) slice.
 *
 *  - 'gap'  — below minimum-viable-size. Cito blueprint may fail. Coach.
 *  - 'thin' — between minimum and comfort. Works today, fragile tomorrow.
 *             Info-level (visible, no urgency).
 *  - 'ok'   — at or above comfort. No issue emitted.
 *
 * Discriminated enum because Audit-Lead principle #2: never collapse three
 * meaningful states into two booleans.
 */
export type AssessmentPoolStatus = 'gap' | 'thin' | 'ok'

// ─── Threshold Resolver ──────────────────────────────────────────────────────

/**
 * Resolve the minimum-viable-size for a (module, tier-band) pair.
 * Honours `MODULE_POOL_OVERRIDES` when present, else falls back to the
 * global `MINIMUM_VIABLE_POOL_SIZE` floor.
 */
export function resolveMinimumViableSize(
  module: ModuleId,
  band: TierBand,
): number {
  const override = MODULE_POOL_OVERRIDES[module]?.[band]
  if (typeof override === 'number') return override
  return MINIMUM_VIABLE_POOL_SIZE[band]
}

/**
 * Classify a pool's MC-eligible count into the AssessmentPoolStatus enum.
 *
 * Pure function — no side effects, no I/O — so it's trivially unit-testable
 * and reusable from admin-UI tooltips that want the same classification.
 */
export function classifyPoolStatus(
  mcEligibleCount: number,
  minimumViableSize: number,
): AssessmentPoolStatus {
  if (mcEligibleCount < minimumViableSize) return 'gap'
  if (mcEligibleCount < minimumViableSize * COMFORT_MULTIPLIER) return 'thin'
  return 'ok'
}

// ─── Pool Distribution Thresholds (Layer B) ──────────────────────────────────

/**
 * Cito-difficulty bucketing.
 *
 * Why 4 uniform quartile buckets over [0.10, 0.85]? The seed convention is
 * IRT-style (higher = harder, range ~0.15–0.75 per AI_SEED_GENERATION_RULES).
 * We pad slightly on both ends so seeds near the edges still bucket cleanly.
 *
 * Layer C (feasibility, later sprint) may replace this with tier-band-specific
 * ranges once `assessmentBlueprintGenerator` dry-runs reveal which buckets
 * actually get sampled per band. For Layer B the uniform split is the right
 * MVP: it catches the worst pathology (all questions clustered at one
 * difficulty) without overfitting to assumptions about per-band ranges.
 *
 * Bucket index 0 = easiest quartile, 3 = hardest.
 */
export const CITO_BUCKET_BOUNDS: Readonly<number[]> = Object.freeze([
  0.1, 0.2875, 0.475, 0.6625, 0.85,
])

export const CITO_BUCKET_COUNT = 4 as const

/**
 * A pool is "concentrated" when one bucket holds too large a share of the
 * (cito-tagged) MC-eligible questions. Two thresholds give us a two-step
 * signal:
 *
 *  - share ≥ HARD → coach (one bucket dominates; blueprint will struggle)
 *  - share ≥ SOFT → info  (lopsided, still usable)
 *  - share <  SOFT → ok   (no issue emitted)
 *
 * Rationale for the cut-points: with 4 equal-weight buckets, a perfectly
 * uniform pool sits at 25% per bucket. 60% means one bucket holds more than
 * twice its fair share; 80% means it holds three times its fair share and the
 * other three buckets together cover less.
 */
export const CITO_CONCENTRATION_SOFT_THRESHOLD = 0.6 as const
export const CITO_CONCENTRATION_HARD_THRESHOLD = 0.8 as const

/**
 * Minimum number of cito-tagged MC-eligible questions in a slice before the
 * cito-concentration check is meaningful. Below this, the bucket distribution
 * is too noisy to draw conclusions from — silently skip.
 */
export const CITO_CONCENTRATION_MIN_SAMPLE = 6 as const

/**
 * Minimum number of distinct DOK levels per tier-band slice for a healthy
 * Cito-style blueprint. A pool that only contains DOK-1 (recall) items cannot
 * serve a DOK-2/3 (application/analysis) blueprint slot, regardless of how
 * many questions it has.
 *
 * Rationale per band:
 *  - basic / intermediate: need at least DOK 1 + DOK 2 → ≥ 2 distinct levels.
 *  - advanced / expert:    need at least DOK 1 + DOK 2 + DOK 3 → ≥ 3 distinct.
 *
 * DOK 4 (extended reasoning) is bonus, never required — the curriculum
 * doesn't standardise on it for ages 9-12.
 */
export const MINIMUM_DOK_COVERAGE: Readonly<Record<TierBand, number>> =
  Object.freeze({
    basic: 2,
    intermediate: 2,
    advanced: 3,
    expert: 3,
  })

/**
 * Distribution finding kinds emitted by Layer B. Discriminator carried as
 * `metadata.reason` on the rollup code `ASSESSMENT_POOL_DISTRIBUTION`,
 * matching the established pattern of CONTENT_CITO_ISSUE,
 * CONTENT_DISTRACTOR_QUALITY, SUPPORT_EXPLANATION_QUALITY, etc.
 *
 *  - 'cito_concentration' — one cito bucket dominates the pool
 *  - 'dok_coverage'       — too few distinct DOK levels represented
 */
export type AssessmentPoolDistributionReason =
  | 'cito_concentration'
  | 'dok_coverage'

// ─── Distribution Classifiers ────────────────────────────────────────────────

/**
 * Place a cito-difficulty value into a bucket (0..CITO_BUCKET_COUNT-1).
 * Returns null when the value is outside CITO_BUCKET_BOUNDS (which means the
 * seed itself is out-of-range — a separate finding, not our concern here).
 *
 * Pure function — also reusable from admin-UI tooltips that want to show
 * "this question sits in bucket 2 (medium-hard)".
 */
export function bucketCitoDifficulty(value: number): number | null {
  const min = CITO_BUCKET_BOUNDS[0]
  const max = CITO_BUCKET_BOUNDS[CITO_BUCKET_BOUNDS.length - 1]
  if (value < min || value > max) return null
  for (let i = 0; i < CITO_BUCKET_COUNT; i++) {
    const lo = CITO_BUCKET_BOUNDS[i]
    const hi = CITO_BUCKET_BOUNDS[i + 1]
    // Last bucket is closed on the right so `max` lands in bucket N-1.
    if (i === CITO_BUCKET_COUNT - 1) {
      if (value >= lo && value <= hi) return i
    } else if (value >= lo && value < hi) {
      return i
    }
  }
  return null
}

/** Concentration status for a cito-bucket distribution. */
export type CitoConcentrationStatus = 'ok' | 'lopsided' | 'dominated'

/**
 * Classify the cito-bucket distribution of a pool.
 *
 *  - 'dominated' — share of the largest bucket ≥ HARD threshold (coach)
 *  - 'lopsided'  — share ≥ SOFT but < HARD threshold (info)
 *  - 'ok'        — otherwise
 *
 * Returns 'ok' for samples below CITO_CONCENTRATION_MIN_SAMPLE so callers can
 * branch on a single enum without re-checking the sample size.
 */
export function classifyCitoConcentration(
  bucketCounts: readonly number[],
  totalSampled: number,
): { status: CitoConcentrationStatus; topBucket: number; topShare: number } {
  if (totalSampled < CITO_CONCENTRATION_MIN_SAMPLE) {
    return { status: 'ok', topBucket: -1, topShare: 0 }
  }
  let topBucket = 0
  let topCount = bucketCounts[0] ?? 0
  for (let i = 1; i < bucketCounts.length; i++) {
    const c = bucketCounts[i] ?? 0
    if (c > topCount) {
      topBucket = i
      topCount = c
    }
  }
  const topShare = totalSampled > 0 ? topCount / totalSampled : 0
  if (topShare >= CITO_CONCENTRATION_HARD_THRESHOLD)
    return { status: 'dominated', topBucket, topShare }
  if (topShare >= CITO_CONCENTRATION_SOFT_THRESHOLD)
    return { status: 'lopsided', topBucket, topShare }
  return { status: 'ok', topBucket, topShare }
}

/** Resolve the DOK coverage minimum for a tier band. */
export function resolveMinimumDokCoverage(band: TierBand): number {
  return MINIMUM_DOK_COVERAGE[band]
}
